import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import GalleryPage from './pages/GalleryPage';
import DegenSlap from './pages/DegenSlap';
import PvPArena from './pages/PvPArena';
import ShopPage from './pages/ShopPage';
import MarketplacePage from './pages/MarketplacePage';
import LeaderboardPage from './pages/LeaderboardPage';
import AdminDashboard from './pages/AdminDashboard';
import WalletDashboard from './pages/WalletDashboard';
import HypeLanding from './pages/HypeLanding';
import SquidGameBridge from './pages/SquidGameBridge';
import { Toaster } from 'sonner';
import NewsTicker from './components/NewsTicker';
import OnboardingBanner from './components/OnboardingBanner';
import SecurityGate from './components/SecurityGate';
import SolanaWalletProvider from './providers/SolanaWalletProvider';
import AuthProvider from './providers/AuthProvider';
import { I18nProvider } from './providers/I18nProvider';
import { PriceOracleProvider } from './providers/PriceOracleProvider';
import { GameWalletProvider } from './providers/GameWalletProvider';
import { CardBuffProvider } from './providers/CardBuffProvider';

// ============================================================
// LAUNCH STATUS: GAME IS NOW LIVE! 🚀
// Set to FALSE to re-enable hype page lock
// Admin bypass still works: ?admin=slap2026
// ============================================================
const GAME_UNLOCKED = true;
const ADMIN_SECRET = 'slap2026';

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isAdminBypass = params.get('admin') === ADMIN_SECRET;
  const hasAdminSession = sessionStorage.getItem('admin_bypass') === 'true';
  
  // Store admin bypass in session
  useEffect(() => {
    if (isAdminBypass) {
      sessionStorage.setItem('admin_bypass', 'true');
    }
  }, [isAdminBypass]);
  
  // If game is unlocked globally, or admin bypass is active
  if (GAME_UNLOCKED || isAdminBypass || hasAdminSession) {
    return children;
  }
  
  // Otherwise redirect to hype page
  return <Navigate to="/" replace />;
};

function App() {
  const [isUnlocked, setIsUnlocked] = useState(false);

  return (
    <SolanaWalletProvider>
      <AuthProvider>
        <I18nProvider>
          <PriceOracleProvider>
            <GameWalletProvider>
            <CardBuffProvider>
              <div className="App">
                <BrowserRouter>
                  {/* Check if game is locked */}
                  {!GAME_UNLOCKED ? (
                    <Routes>
                      {/* Public: Hype Landing Page */}
                      <Route path="/" element={<HypeLanding />} />
                      
                      {/* Admin bypass routes - require ?admin=slap2026 or session */}
                      <Route path="/game/*" element={
                        <ProtectedRoute>
                          <SecurityGate onUnlock={() => setIsUnlocked(true)}>
                            <Routes>
                              <Route path="/" element={<Home />} />
                              <Route path="/gallery" element={<GalleryPage />} />
                              <Route path="/pvp" element={<DegenSlap />} />
                              <Route path="/arena" element={<PvPArena />} />
                              <Route path="/shop" element={<ShopPage />} />
                              <Route path="/marketplace" element={<MarketplacePage />} />
                              <Route path="/leaderboard" element={<LeaderboardPage />} />
                              <Route path="/admin" element={<AdminDashboard />} />
                              <Route path="/wallet" element={<WalletDashboard />} />
                            </Routes>
                            <NewsTicker className="fixed bottom-0 left-0 right-0 z-50" />
                            <OnboardingBanner />
                          </SecurityGate>
                        </ProtectedRoute>
                      } />
                      
                      {/* Redirect all other routes to hype page */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  ) : (
                    /* Game is unlocked - normal routing */
                    <SecurityGate onUnlock={() => setIsUnlocked(true)}>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/gallery" element={<GalleryPage />} />
                        <Route path="/pvp" element={<DegenSlap />} />
                        <Route path="/degen-slap" element={<DegenSlap />} />
                        <Route path="/arena" element={<PvPArena />} />
                        <Route path="/shop" element={<ShopPage />} />
                        <Route path="/marketplace" element={<MarketplacePage />} />
                        <Route path="/leaderboard" element={<LeaderboardPage />} />
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/wallet" element={<WalletDashboard />} />
                        <Route path="/squid-game" element={<SquidGameBridge />} />
                        <Route path="/bridge" element={<SquidGameBridge />} />
                      </Routes>
                      <NewsTicker className="fixed bottom-0 left-0 right-0 z-50" />
                      <OnboardingBanner />
                    </SecurityGate>
                  )}
                </BrowserRouter>
              </div>
              <Toaster
                position="top-center"
                toastOptions={{
                  style: {
                    background: '#0A0A0A',
                    color: '#FFFFFF',
                    border: '2px solid #FF00FF',
                    fontFamily: 'Space Mono, monospace',
                  },
                }}
              />
            </CardBuffProvider>
            </GameWalletProvider>
          </PriceOracleProvider>
        </I18nProvider>
      </AuthProvider>
    </SolanaWalletProvider>
  );
}

export default App;